import { zodResolver } from "@hookform/resolvers/zod";
import { cn, type FileUploadData, useToast } from "@qw/ui";
import type { FileUIPart } from "ai";
import {
  type BaseSyntheticEvent,
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useLocalStorage } from "usehooks-ts";

import { isChatSessionBusy } from "../../chat.helpers";
import { useChat } from "../../providers";
import { ChatFormSchema, type ChatFormValues } from "./chat-form.types";
import { useChatFilesDropzone } from "./use-chat-files-dropzone";
import { ChatFormContext } from "./use-chat-form";
import { useChatTranscription } from "./use-chat-transcription";

const defaultValues: ChatFormValues = {
  text: "",
  files: [],
};

const STORAGE_KEY_PREFIX = "chat-form-values-";

const getStorageKey = (sessionId: string) => `${STORAGE_KEY_PREFIX}${sessionId}`;

export const ChatFormProvider: FC<PropsWithChildren<{ className?: string }>> = ({ children, className }) => {
  const { apiUrl, chatSession, isReadOnly, status, sendMessage, resumeAutoScroll, isElicitationSubmitting } = useChat();
  const { toast } = useToast();

  // Persist form values to localStorage (per chat session)
  const [storedValues, setStoredValues, removeStoredValues] = useLocalStorage<ChatFormValues>(
    getStorageKey(chatSession.id),
    defaultValues
  );

  // Validate each field individually to preserve valid data even if other fields are corrupted
  // (handles corrupted localStorage, manual tampering, or schema migrations)
  const sanitizedDefaultValues = useMemo<ChatFormValues>(() => {
    const textResult = ChatFormSchema.shape.text.safeParse(storedValues?.text);
    const filesResult = ChatFormSchema.shape.files.safeParse(storedValues?.files);

    return {
      text: textResult.success ? textResult.data : defaultValues.text,
      files: filesResult.success ? filesResult.data : defaultValues.files,
    };
  }, [storedValues]);

  const formMethods = useForm<ChatFormValues>({
    mode: "onTouched",
    resolver: zodResolver(ChatFormSchema as any),
    defaultValues: sanitizedDefaultValues,
  });

  // Subscribe to form value changes and persist to localStorage
  useEffect(() => {
    const { unsubscribe } = formMethods.watch((values) => {
      // Filter out temp_ files that are still uploading
      const filesToPersist = (values.files ?? []).filter((file) => file && !file.id?.startsWith("temp_"));

      if (values.text?.trim() || filesToPersist.length > 0) {
        setStoredValues({ text: values.text ?? "", files: filesToPersist as FileUploadData[] });
      } else {
        // Remove from localStorage when empty (cleanup)
        removeStoredValues();
      }
    });

    return () => unsubscribe();
  }, [formMethods, setStoredValues, removeStoredValues]);

  // TODD: stop recording also calls this. We should not call this when recording is stopped.
  // Instead, we should allow passing in a different method to handle transcription stopped event, to allow us to stop transcription without updating.
  const handleTranscriptUpdate = useCallback((text: string) => formMethods.setValue("text", text), [formMethods]);

  const { startRecording, stopRecording, cancelRecording, isIdle, isRecording, isProcessing, isError, audioLevels } =
    useChatTranscription({ apiUrl, chatSessionId: chatSession.id, onTranscriptUpdate: handleTranscriptUpdate });

  // Refs
  const textInputRef = useRef<HTMLTextAreaElement | null>(null);
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);

  const input = useWatch({ control: formMethods.control, name: "text" });
  const setInput = useCallback((newValue: string) => formMethods.setValue("text", newValue), [formMethods]);

  const files = useWatch({ control: formMethods.control, name: "files" });
  const { append, remove, replace } = useFieldArray({ control: formMethods.control, name: "files" });
  const setFiles = useCallback((newValue: FileUploadData[]) => replace(newValue), [replace]);
  const addFile = useCallback((file: FileUploadData) => append(file), [append]);
  const removeFile = useCallback((index: number) => remove(index), [remove]);
  const clearFiles = useCallback(() => replace([]), [replace]);
  const isUploadingFiles = useMemo(() => files.some((file) => file.id.startsWith("temp_")), [files]);

  const isStreaming = useMemo(() => ["submitted", "streaming"].includes(status), [status]);

  // Server-busy: chat session is mid-work where this client isn't
  // consuming a stream (child playbook running, fresh trigger-
  // dispatched turn, resume in flight). See `isChatSessionBusy` for
  // the exact status set and the AWAITING_USER_INPUT exclusion
  // rationale.
  const isSessionBusy = isChatSessionBusy(chatSession?.status);

  const hasInput = useMemo(() => !!input?.trim(), [input]);
  const hasFiles = useMemo(() => files.length > 0 && files.every((file) => !file.id.startsWith("temp_")), [files]);

  const canEdit = useMemo(
    () => !isReadOnly && !isStreaming && !isProcessing && !isElicitationSubmitting && !isSessionBusy,
    [isReadOnly, isProcessing, isStreaming, isElicitationSubmitting, isSessionBusy]
  );
  const canSubmit = useMemo(
    () => canEdit && !isUploadingFiles && (hasInput || hasFiles),
    [canEdit, hasInput, hasFiles, isUploadingFiles]
  );

  // Files dropzone
  const { dropzone } = useChatFilesDropzone({ formMethods, disabled: !canEdit });

  const handleSubmit = useCallback(
    async (values: ChatFormValues) => {
      resumeAutoScroll();

      // Allow submit with just files (no text required)
      if (!canSubmit) {
        return;
      }

      // Convert attached files to FileUIPart format
      const fileUIParts: FileUIPart[] = values.files.map((f) => ({
        type: "file",
        mediaType: f.mimeType,
        filename: f.filename,
        url: `/api/files/${f.id}`,
      }));

      // If we are recording, stop recording and add the transcript to the message
      if (isRecording) {
        const finalTranscript = await stopRecording();
        values.text = finalTranscript || values.text;
      }

      // Reset optimistically, but restore on error
      // Note: Pass defaultValues explicitly since form's defaultValues is storedValues
      formMethods.reset(defaultValues);

      try {
        // Use SDK's built-in files support
        await sendMessage({
          text: values.text,
          files: fileUIParts.length > 0 ? fileUIParts : undefined,
        });
      } catch (error) {
        // Restore user input on failure
        formMethods.reset(values);
        toast({
          variant: "destructive",
          title: "Failed to send message",
          description: error instanceof Error ? error.message : "Please try again",
        });
      }
    },
    [canSubmit, isRecording, stopRecording, sendMessage, resumeAutoScroll, formMethods, toast]
  );

  const submitForm = useCallback(
    (event?: BaseSyntheticEvent) => formMethods.handleSubmit(handleSubmit)(event),
    [formMethods, handleSubmit]
  );

  const value = useMemo(
    () => ({
      // React Hook Form methods
      formMethods,

      // Text input
      input,
      setInput,

      // Files and dropzone
      files,
      setFiles,
      addFile,
      removeFile,
      clearFiles,
      isUploadingFiles,
      dropzone,

      // Transcription
      startRecording,
      stopRecording,
      cancelRecording,
      isIdle,
      isRecording,
      isProcessing,
      isError,
      audioLevels,

      // Form submission
      submitForm,

      // State helpers
      isStreaming,
      hasInput,
      hasFiles,
      canEdit,
      canSubmit,

      // Refs
      textInputRef,
      submitButtonRef,
    }),
    [
      // React Hook Form methods
      formMethods,

      // Text input
      input,
      setInput,

      // Files and dropzone
      files,
      setFiles,
      addFile,
      removeFile,
      clearFiles,
      isUploadingFiles,
      dropzone,

      // Transcription
      startRecording,
      stopRecording,
      cancelRecording,
      isIdle,
      isRecording,
      isProcessing,
      isError,
      audioLevels,

      // Form submission
      submitForm,

      // State helpers
      isStreaming,
      hasInput,
      hasFiles,
      canEdit,
      canSubmit,
    ]
  );

  return (
    <FormProvider {...formMethods}>
      <ChatFormContext.Provider value={value}>
        <form onSubmit={submitForm} className={cn("flex flex-col flex-1", className)}>
          {children}
        </form>
      </ChatFormContext.Provider>
    </FormProvider>
  );
};
